import type {
  ProductDetailPageGalleryImage,
  ProductDetailPageGalleryItem,
} from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/ProductDetailPageGallery.types'
import { MOBILE_AUTOMATED_REVIEWS_MAX_COUNT } from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/composables/useGalleryLayout/useGalleryLayout.constants'

function toImageItem(
  image: ProductDetailPageGalleryImage,
  size: 'small' | 'large',
): ProductDetailPageGalleryItem {
  return {
    kind: 'image',
    image,
    size,
  }
}

export function buildMobileGalleryLayout({
  baseImages,
  injectableItems,
}: {
  baseImages: ProductDetailPageGalleryImage[]
  injectableItems: ProductDetailPageGalleryItem[]
}): ProductDetailPageGalleryItem[] {
  if (baseImages.length === 0) {
    return []
  }

  const reviews = injectableItems.filter(
    (item) => item.kind === 'review' || item.kind === 'skeleton',
  )
  const videoItem = injectableItems.find(
    (item): item is Extract<ProductDetailPageGalleryItem, { kind: 'video' }> =>
      item.kind === 'video',
  )

  const cappedReviews = reviews.slice(0, MOBILE_AUTOMATED_REVIEWS_MAX_COUNT)

  // 1. First image always large
  const items: ProductDetailPageGalleryItem[] = [
    toImageItem(baseImages[0], 'large'),
  ]

  // 2. Video always large, always 2nd if present
  if (videoItem) {
    items.push({ ...videoItem, size: 'large' })
  }

  // 3. Reviews
  let remainingStartIndex = 1

  if (cappedReviews.length >= 2) {
    items.push(
      { ...cappedReviews[0], size: 'small' },
      { ...cappedReviews[1], size: 'small' },
    )
    if (cappedReviews[2]) {
      items.push({ ...cappedReviews[2], size: 'large' })
    }
  } else if (cappedReviews.length === 1) {
    const imageCount = baseImages.length

    if (imageCount >= 3) {
      items.push(
        toImageItem(baseImages[1], 'small'),
        toImageItem(baseImages[2], 'small'),
        { ...cappedReviews[0], size: 'large' },
      )
      remainingStartIndex = 3
    } else if (imageCount === 2) {
      items.push(
        { ...cappedReviews[0], size: 'large' },
        toImageItem(baseImages[1], 'large'),
      )
      remainingStartIndex = 2
    } else {
      items.push({ ...cappedReviews[0], size: 'large' })
    }
  }

  // 4. Remaining images in bento L,S,S pattern
  const remainingImages = baseImages.slice(remainingStartIndex)
  remainingImages.forEach((image, j) => {
    items.push(toImageItem(image, j % 3 === 0 ? 'large' : 'small'))
  })

  // 5. Orphan fix: any lone small → promote to large
  for (let i = 0; i < items.length; i += 1) {
    if (items[i].size === 'small') {
      const prev = items[i - 1]
      const next = items[i + 1]
      const hasSmallNeighbor =
        (prev && prev.size === 'small') || (next && next.size === 'small')

      if (!hasSmallNeighbor) {
        items[i].size = 'large'
      }
    }
  }

  return items
}

export function buildDesktopGalleryLayout({
  baseImages,
  injectableItems,
}: {
  baseImages: ProductDetailPageGalleryImage[]
  injectableItems: ProductDetailPageGalleryItem[]
}): ProductDetailPageGalleryItem[] {
  if (baseImages.length === 0) {
    return []
  }

  const reviews = injectableItems.filter(
    (item) => item.kind === 'review' || item.kind === 'skeleton',
  )
  const videoItem = injectableItems.find(
    (item): item is Extract<ProductDetailPageGalleryItem, { kind: 'video' }> =>
      item.kind === 'video',
  )

  // Position 1: First image always large
  const items: ProductDetailPageGalleryItem[] = [
    toImageItem(baseImages[0], 'large'),
  ]

  let nextImageIndex = 1

  // Position 2: video if present, else next image
  if (videoItem) {
    items.push({ ...videoItem, size: 'small' })
  } else if (nextImageIndex < baseImages.length) {
    items.push(toImageItem(baseImages[nextImageIndex], 'small'))
    nextImageIndex += 1
  }

  // Position 3: next image if available (skip when video is present to keep reviews in 3rd position)
  if (!videoItem && nextImageIndex < baseImages.length) {
    items.push(toImageItem(baseImages[nextImageIndex], 'small'))
    nextImageIndex += 1
  }

  // All reviews (small)
  for (const review of reviews) {
    items.push({ ...review, size: 'small' })
  }

  // Remaining images (small)
  while (nextImageIndex < baseImages.length) {
    items.push(toImageItem(baseImages[nextImageIndex], 'small'))
    nextImageIndex += 1
  }

  return items
}
